import { Component, OnInit, ChangeDetectionStrategy, inject, viewChild, input, linkedSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { ImageCropperComponent } from './image-cropper.component';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-image-cropper-dialog',
  templateUrl: './image-cropper-dialog.component.html',
  styleUrls: ['./image-cropper-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogTitle, CdkScrollable, MatDialogContent, ImageCropperComponent, MatButton, MatIcon]
})
export class ImageCropperDialogComponent implements OnInit {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<ImageCropperDialogComponent>>(MatDialogRef);

  public readonly cropper = viewChild(ImageCropperComponent);

  public readonly fileInput = input<any>(undefined, {alias: 'file'});
  readonly file = linkedSignal(() => this.fileInput());
  public readonly filenameInput = input<any>(undefined, {alias: 'filename'});
  readonly filename = linkedSignal(() => this.filenameInput());
  public readonly cropConfigInput = input<any>(undefined, {alias: 'cropConfig'});
  readonly cropConfig = linkedSignal(() => this.cropConfigInput());

  public cropConfigDefault: any = {
    movable: true,
    scalable: true,
    zoomable: true,
    viewMode: 1,
    autoCrop: true,
    autoCropArea: 1,
    checkCrossOrigin: true,
    aspectRatio: 4 / 3,
    portrait: false,
    landscape: false
  };

  ngOnInit() {
    //
    // Join crop config
    this.cropConfig.set({ ...this.cropConfigDefault, ...this.data?.cropConfig, ...this.cropConfig() });

    //
    // Set data
    this.file.set(this.data.file);
    this.filename.set(this.data.filename);
  }

  finish() {
    //
    // Get cropped canvas
    this.cropper().cropper.getCroppedCanvas().toBlob(blob => {
      //
      // transform in file object
      const file = new File([blob], this.filename(), {type: 'image/png'});

      //
      // Emit on crop event with file
      this.dialogRef.close({
        success: true,
        file
      });
    });
  }

  /**
   * Zoom in
   */
  zoomIn() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.zoom(0.1);
  }

  /**
   * Zoom out
   */
  zoomOut() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.zoom(-0.1);
  }

  /**
   * Go left
   */
  goLeft() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.move(-10, 0);
  }

  /**
   * Go right
   */
  goRight() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.move(10, 0);
  }

  /**
   * Go up
   */
  goUp() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.move(0, -10);
  }

  /**
   * Go down
   */
  goDown() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.move(0, 10);
  }

  /**
   * Rotate left
   */
  rotateLeft() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.rotate(-45);
  }

  /**
   * Rotate right
   */
  rotateRight() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.rotate(45);
  }

  /**
   * Reset
   */
  reset() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.reset();
  }

  /**
   * Set landscape
   */
  setLandscape() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Reset
    this.reset();

    //
    // Action
    cropper.cropper.setAspectRatio(this.cropConfig().landscape);
  }

  /**
   * Set portrait
   */
  setPortrait() {
    //
    // Validate
    const cropper = this.cropper();
    if (!cropper || !cropper.cropper) {
      return;
    }

    //
    // Action
    cropper.cropper.setAspectRatio(this.cropConfig().portrait);
  }
}
